import { HttpClient, httpResource, HttpResourceRef } from "@angular/common/http";
import { inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { firstValueFrom } from "rxjs";
import {
    ApplyIdentityProviderCertificateRequest,
    AvailableIdentityProviderCertificate,
    AvailableIdentityProviderCertificatesResponse,
    NetworkIdentityProvider,
    NetworkIdentityProviderConfiguration,
    NetworkIdentityProviderProvisioning,
    NetworkIdentityProviderProvisioningConfiguration,
    NetworkIdentityProviderType,
    ProvisioningTokenResponse,
    SaveNetworkIdentityProviderRequest
} from "./network-identity-providers.models";

@Injectable({
    providedIn: "root"
})
export class NetworkIdentityProvidersService {
    private readonly http = inject(HttpClient);

    private readonly _networkIdentityProvider: HttpResourceRef<NetworkIdentityProvider | null> = httpResource(() => ({
        url: "/api/networks/identityprovider"
    }));

    public getNetworkIdentityProviderTypes = (): HttpResourceRef<NetworkIdentityProviderType[]> =>
        httpResource(() => ({
            url: "/api/networks/identityprovider/supportedtypes"
        }));

    public getNetworkIdentityProvider = (): HttpResourceRef<NetworkIdentityProvider | null> => this._networkIdentityProvider;

    public saveNetworkIdentityProviderAsync(request: SaveNetworkIdentityProviderRequest): Promise<NetworkIdentityProvider> {
        return firstValueFrom(this.http.post<NetworkIdentityProvider>("/api/networks/identityprovider", request));
    }

    public async deleteNetworkIdentityProviderAsync(): Promise<void> {
        await firstValueFrom(this.http.delete<void>("/api/networks/identityprovider"));
    }

    public async getNetworkIdentityProviderConfigurationAsync(typeName: string): Promise<NetworkIdentityProviderConfiguration> {
        return firstValueFrom(this.http.get<NetworkIdentityProviderConfiguration>(`/api/networks/identityprovider/supportedtypes/${typeName}/configuration`));
    }

    public getProvisioningInfo(): Signal<NetworkIdentityProviderProvisioning> {
        return toSignal(this.http.get<NetworkIdentityProviderProvisioning>("/api/networks/identityprovider/provisioning"));
    }

    public async getProvisioningConfigurationForIdentityProviderTypeAsync(typeName: string): Promise<NetworkIdentityProviderProvisioningConfiguration[]> {
        return firstValueFrom(
            this.http.get<NetworkIdentityProviderProvisioningConfiguration[]>(
                `/api/networks/identityprovider/supportedtypes/${typeName}/provisioning/configuration`
            )
        );
    }

    public async generateProvisioningTokenAsync(): Promise<ProvisioningTokenResponse> {
        return firstValueFrom(this.http.post<ProvisioningTokenResponse>("/api/networks/identityprovider/provisioning/token", {}));
    }

    public async deleteProvisioningTokenAsync(): Promise<void> {
        return firstValueFrom(this.http.delete<void>("/api/networks/identityprovider/provisioning/token"));
    }

    public async fetchAvailableCertificatesAsync(): Promise<AvailableIdentityProviderCertificate[]> {
        const response = await firstValueFrom(this.http.get<AvailableIdentityProviderCertificatesResponse>("/api/networks/identityprovider/certificates"));
        return response.items;
    }

    public async applyCertificateAsync(request: ApplyIdentityProviderCertificateRequest): Promise<NetworkIdentityProvider> {
        return firstValueFrom(this.http.post<NetworkIdentityProvider>("/api/networks/identityprovider/certificates/apply", request));
    }
}
